import { ResolvedMatch, Employee, MeetingAttendee, AttendeePriority, SortOption } from '../interfaces/interface';
import { MEETING_CONSTANTS } from '../constants/EAHuddleConstants';
import { EmployeeService } from '../services/EmployeeService';
import { LeaderEAConfigService } from '../services/LeaderEAConfigService';

/**
 * Compute the denormalized set of logins authorized to access a meeting.
 * Includes: creator, organizer, onBehalfOf, all attendee logins, and all EA logins.
 */
export const computeAuthorizedLogins = (
  creatorAlias: string,
  attendees: MeetingAttendee[],
  options?: { organizerLogin?: string; onBehalfOfLogin?: string | null; coOrganizerLogin?: string | null }
): string[] => {
  const logins = new Set<string>();
  logins.add(creatorAlias.toLowerCase());
  if (options?.organizerLogin) logins.add(options.organizerLogin.toLowerCase());
  if (options?.onBehalfOfLogin) logins.add(options.onBehalfOfLogin.toLowerCase());
  if (options?.coOrganizerLogin) logins.add(options.coOrganizerLogin.toLowerCase());

  const leaderConfigs = LeaderEAConfigService.getAllCached();

  for (const a of attendees) {
    if (a.loginName) logins.add(a.loginName.toLowerCase());
    if (a.eaLogin) logins.add(a.eaLogin.toLowerCase());
    if (a.originalEaLogin) logins.add(a.originalEaLogin.toLowerCase());
    // Add overrideMembers from leader EA config for leaders present as attendees
    const leaderEntry = leaderConfigs.find((e) => e.leaderAlias.toLowerCase() === a.loginName?.toLowerCase());
    if (leaderEntry?.overrideMembers) {
      for (const member of leaderEntry.overrideMembers) {
        logins.add(member.toLowerCase());
      }
    }
  }
  return Array.from(logins);
};

/**
 * Compute the list of EA ANT group IDs relevant to a meeting's attendees.
 * Returns deduplicated group entries for entries with a non-null groupType.
 * Used to persist on the meeting record for read-time access checks.
 */
export const computeEAGroupIds = (attendees: MeetingAttendee[]): Array<{ groupId: string; groupType: string }> => {
  const seen = new Set<string>();
  const groups: Array<{ groupId: string; groupType: string }> = [];
  const leaderConfigs = LeaderEAConfigService.getAllCached();

  for (const attendee of attendees) {
    // Include directly-added email list groups
    if (attendee.isGroup && attendee.groupType && !seen.has(attendee.loginName)) {
      seen.add(attendee.loginName);
      groups.push({ groupId: attendee.loginName, groupType: attendee.groupType });
      continue;
    }
    // Include groups from leader EA config
    const entry = leaderConfigs.find((e) => e.leaderAlias.toLowerCase() === attendee.loginName?.toLowerCase());
    if (entry?.groupType && entry.groupId && !seen.has(entry.groupId)) {
      seen.add(entry.groupId);
      groups.push({ groupId: entry.groupId, groupType: entry.groupType });
    }
  }
  return groups;
};

/**
 * Build MeetingAttendee list from resolved attendees, including eaLogin.
 */
export const buildMeetingAttendees = (resolvedAttendees: ResolvedMatch[]): MeetingAttendee[] => {
  return resolvedAttendees
    .filter((a) => a.selectedEmployee?.login_name || a.isGroup)
    .map((a) => {
      if (a.isGroup) {
        return {
          loginName: a.inputName,
          priority: a.priority,
          isGroup: true,
          groupType: a.groupType ?? 'antGroup'
        };
      }
      return {
        loginName: a.selectedEmployee!.login_name,
        priority: a.priority,
        eaLogin: a.selectedExecutiveAssistant?.login_name || undefined,
        employeeName: a.selectedEmployee!.employee_name || undefined,
        proxyForLogin: a.proxyForLogin || undefined,
        proxyForName: a.proxyForName || undefined,
        ...(a.isEaOverridden ? { isEaOverridden: true, originalEaLogin: a.originalEaLogin } : {})
      };
    });
};

/**
 * Normalizes a login name to lowercase for consistent comparison.
 */
export const normalizeLoginName = (employee: Employee): string => {
  return employee.login_name?.toLowerCase() ?? '';
};

/**
 * Checks if an employee already exists in the attendees list based on login name.
 */
export const isDuplicateEmployee = (employee: Employee, currentAttendees: ResolvedMatch[]): boolean => {
  const loginName = normalizeLoginName(employee);
  if (!loginName) return false;

  return currentAttendees.some((attendee) => {
    const existingLoginName = attendee.selectedEmployee?.login_name?.toLowerCase();
    return existingLoginName === loginName;
  });
};

const PRIORITY_ORDER: Record<string, number> = { critical: 0, required: 1, optional: 2 };

const comparePriority = (a: ResolvedMatch, b: ResolvedMatch): number => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];

const compareLevel = (a: ResolvedMatch, b: ResolvedMatch): number => {
  const aLevel = parseInt(a.selectedEmployee?.job_level_name || '0');
  const bLevel = parseInt(b.selectedEmployee?.job_level_name || '0');
  return bLevel - aLevel;
};

const compareName = (a: ResolvedMatch, b: ResolvedMatch): number => {
  const aName = a.selectedEmployee?.employee_name || a.inputName;
  const bName = b.selectedEmployee?.employee_name || b.inputName;
  return aName.localeCompare(bName);
};

const SORT_COMPARATORS: Record<SortOption, ((a: ResolvedMatch, b: ResolvedMatch) => number)[]> = {
  priority: [comparePriority, compareLevel, compareName],
  level: [compareLevel, comparePriority, compareName],
  name: [compareName, comparePriority, compareLevel]
};

/**
 * Sorts attendees with a configurable primary sort dimension.
 * Unresolved attendees always appear first. Proxy adjacency is preserved.
 *
 * @param sortBy - Primary sort dimension: 'priority' (default), 'level', or 'name'.
 *   The other two dimensions serve as tiebreakers in their original order.
 */
export const sortAttendees = (attendees: ResolvedMatch[], sortBy: SortOption = 'priority'): ResolvedMatch[] => {
  // When sorting by level, pin groups at top (they have no level metadata)
  const pinGroups = sortBy === 'level';
  const groups = pinGroups ? attendees.filter((a) => a.isGroup).sort(comparePriority) : [];
  const individuals = pinGroups ? attendees.filter((a) => !a.isGroup) : attendees;

  const principals = individuals.filter((a) => !a.proxyForLogin);
  const proxies = individuals.filter((a) => a.proxyForLogin);

  const chain = SORT_COMPARATORS[sortBy];

  const sorted = [...principals].sort((a, b) => {
    const aResolved = a.selectedEmployee || a.isGroup;
    const bResolved = b.selectedEmployee || b.isGroup;

    if (!aResolved && bResolved) return -1;
    if (!bResolved && aResolved) return 1;
    if (!aResolved && !bResolved) return 0;

    for (const cmp of chain) {
      const result = cmp(a, b);
      if (result !== 0) return result;
    }
    return 0;
  });

  // Insert proxies immediately after their principal
  const result: ResolvedMatch[] = [];
  for (const principal of sorted) {
    result.push(principal);
    const login = principal.selectedEmployee?.login_name?.toLowerCase();
    if (login) {
      const matchingProxies = proxies.filter((p) => p.proxyForLogin?.toLowerCase() === login);
      result.push(...matchingProxies);
    }
  }

  // Append any orphaned proxies (principal was removed)
  const placed = new Set(result.map((a) => a.id));
  for (const proxy of proxies) {
    if (!placed.has(proxy.id)) result.push(proxy);
  }

  return [...groups, ...result];
};

/**
 * Finds duplicate email addresses in the attendees list.
 */
export const findDuplicateEmails = (attendees: ResolvedMatch[]): Set<string> => {
  const emailCounts = new Map<string, number>();
  const duplicates = new Set<string>();

  attendees.forEach((attendee) => {
    const email = attendee.resolvedEmail?.toLowerCase();
    if (email) {
      const count = emailCounts.get(email) || 0;
      emailCounts.set(email, count + 1);
      if (count >= 1) {
        duplicates.add(email);
      }
    }
  });

  return duplicates;
};

/**
 * Gets all existing email addresses from attendees.
 */
export const getExistingEmails = (attendees: ResolvedMatch[]): Set<string> => {
  return new Set(attendees.map((a) => a.resolvedEmail?.toLowerCase()).filter(Boolean) as string[]);
};

/**
 * Checks if there are any unresolved attendee names.
 */
export const hasUnresolvedNames = (attendees: ResolvedMatch[]): boolean => {
  return attendees.some((attendee) => !attendee.selectedEmployee);
};

/**
 * Load attendees in rate-limited batches to stay under the PAPI 50 TPS limit.
 *
 * Attendees with a valid browser cache entry are loaded immediately (no delay),
 * since they won't hit PAPI. Only cache misses go through rate-limited batching.
 *
 * Each `addAttendee` call triggers ~2 PAPI requests (employee lookup + EA lookup).
 * With batch size 5 that's ~10 PAPI calls/sec/user — well within limits.
 *
 * @param attendeeList - The attendees to load
 * @param addAttendee - Callback that triggers the actual employee resolution
 * @param onProgress - Optional progress callback (e.g. for UI feedback)
 */
export const loadAttendeesInBatches = async (
  attendeeList: MeetingAttendee[],
  addAttendee: (
    employee: Employee,
    priority: AttendeePriority,
    extraFields?: { proxyForLogin?: string; proxyForName?: string; isEaOverridden?: boolean; eaLogin?: string; originalEaLogin?: string }
  ) => Promise<void> | void,
  onProgress?: (message: string) => void
): Promise<void> => {
  const { ATTENDEE_BATCH_SIZE, BATCH_DELAY_MS } = MEETING_CONSTANTS;

  // Filter out group entries — they don't need PAPI resolution
  const individualAttendees = attendeeList.filter((a) => !a.isGroup);
  const total = individualAttendees.length;

  // Split into cache hits (instant) and misses (need rate-limited PAPI calls)
  const cached: MeetingAttendee[] = [];
  const uncached: MeetingAttendee[] = [];
  for (const attendee of individualAttendees) {
    if (EmployeeService.hasBrowserCache(attendee.loginName)) {
      cached.push(attendee);
    } else {
      uncached.push(attendee);
    }
  }

  const toEmployee = (attendee: MeetingAttendee): Employee => ({
    login_name: attendee.loginName,
    employee_name: '',
    employee_internal_email_address: '',
    job_title_name: '',
    location_city_name: '',
    location_state_name: '',
    job_level_name: '',
    department_name_current: ''
  });

  const toExtraFields = (attendee: MeetingAttendee) => {
    const fields: { proxyForLogin?: string; proxyForName?: string; isEaOverridden?: boolean; eaLogin?: string; originalEaLogin?: string } = {};
    if (attendee.proxyForLogin) {
      fields.proxyForLogin = attendee.proxyForLogin;
      fields.proxyForName = attendee.proxyForName;
    }
    if (attendee.isEaOverridden) {
      fields.isEaOverridden = true;
      fields.eaLogin = attendee.eaLogin;
      fields.originalEaLogin = attendee.originalEaLogin;
    }
    return Object.keys(fields).length > 0 ? fields : undefined;
  };

  // Load cache hits immediately — no PAPI calls, no delay needed
  if (cached.length > 0) {
    onProgress?.(`Loading attendees (${cached.length}/${total}) from cache...`);
    await Promise.all(cached.map((a) => addAttendee(toEmployee(a), a.priority, toExtraFields(a))));
  }

  // Rate-limit only the cache misses
  for (let i = 0; i < uncached.length; i += ATTENDEE_BATCH_SIZE) {
    const batch = uncached.slice(i, i + ATTENDEE_BATCH_SIZE);
    const loaded = cached.length + Math.min(i + ATTENDEE_BATCH_SIZE, uncached.length);
    onProgress?.(`Loading attendees (${loaded}/${total})...`);

    await Promise.all(batch.map((a) => addAttendee(toEmployee(a), a.priority, toExtraFields(a))));

    // Delay between batches to respect rate limits
    if (i + ATTENDEE_BATCH_SIZE < uncached.length) {
      await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS));
    }
  }
};

// Re-export from shared location for backward compatibility
export { generateMultiBadgerUrl } from '../../shared/urlUtils';
